/**
 * Utility to be included in iframe content to handle automatic resizing
 * 
 * Usage in your iframe content:
 * 
 * ```ts
 * import { setupIframeContentResize } from 'smarter-elements/utils/iframeContentResize';
 * 
 * // Basic usage
 * setupIframeContentResize();
 * 
 * // With options
 * setupIframeContentResize({
 *   minHeight: 200,    // Minimum height in pixels (default: 100)
 *   offset: 10,        // Additional height to add (default: 0)
 *   elementId: '123'   // Optional element ID for targeted messages
 * });
 * ```
 */

export interface ResizeMessage {
  type: 'resize' | 'getHeight';
  height?: number;
  elementId?: string;
  [key: string]: unknown;
}

/**
 * Setup iframe content to send resize events
 * This should be called from within the iframe content
 * @param options - Options for the resize observer
 * @param options.minHeight - Minimum height in pixels (default: 100)
 * @param options.offset - Additional height to add to the content height (default: 0)
 * @param options.elementId - Optional element ID for targeted messages
 */
export function setupIframeContentResize(options: {
  minHeight?: number;
  offset?: number;
  elementId?: string;
} = {}): () => void {
  console.log('HEIGHT_DEBUG - setupIframeContentResize called with options:', options);
  // Only run in browser environment
  if (typeof window === 'undefined' || !window.parent) {
    console.warn('HEIGHT_DEBUG - setupIframeContentResize should only be called in a browser environment inside an iframe');
    return () => {};
  }

  const {
    minHeight = 100,
    offset = 0,
    elementId,
  } = options;

  let lastHeight = 0;
  let animationFrameId: number | null = null;

  const calculateAndSendHeight = () => {
    try {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      // Use requestAnimationFrame to batch rapid updates
      animationFrameId = requestAnimationFrame(() => {
        try {
          // Get all possible height measurements
          const bodyHeight = document.body.offsetHeight;
          const htmlHeight = document.documentElement.offsetHeight;
          const bodyScrollHeight = document.body.scrollHeight;
          const htmlScrollHeight = document.documentElement.scrollHeight;
          const scrollHeight = Math.max(bodyScrollHeight, htmlScrollHeight);
          
          // Calculate the base height
          const baseHeight = Math.max(bodyHeight, htmlHeight, scrollHeight);
          
          // Apply offset and buffer
          const heightWithOffset = baseHeight + offset;
          const heightWithBuffer = heightWithOffset + 3; // Adding 3px buffer
          let height = Math.max(heightWithBuffer, minHeight);
          
          // Enhanced logging - using console.log for better visibility
          console.log('HEIGHT_DEBUG - Calculation details:', {
            measurements: { bodyHeight, htmlHeight, bodyScrollHeight, htmlScrollHeight },
            calculated: { baseHeight, heightWithOffset, heightWithBuffer, finalHeight: height },
            constraints: { offset, minHeight },
            computedStyles: {
              bodyMargin: window.getComputedStyle(document.body).margin,
              htmlMargin: window.getComputedStyle(document.documentElement).margin,
              bodyPadding: window.getComputedStyle(document.body).padding,
              htmlPadding: window.getComputedStyle(document.documentElement).padding,
            }
          });
          
          // Only send if height changed significantly (more than 1px)
          if (Math.abs(height - lastHeight) > 1) {
            lastHeight = height;
            
            const message: ResizeMessage = {
              type: 'resize',
              height,
            };
            
            if (elementId) {
              message.elementId = elementId;
            }
            
            console.log('Sending resize message to parent:', message);
            window.parent.postMessage(message, '*');
          }
        } catch (error) {
          console.error('HEIGHT_DEBUG - Error in animation frame:', error);
        }
      });
    } catch (error) {
      console.error('Error in calculateAndSendHeight:', error);
    }
  };

  // Create and configure the ResizeObserver
  const resizeObserver = new ResizeObserver(calculateAndSendHeight);

  // Observe the document element and body
  resizeObserver.observe(document.documentElement);
  resizeObserver.observe(document.body);

  // Also observe any elements with data-observe-resize attribute
  const observedElements = document.querySelectorAll('[data-observe-resize]');
  observedElements.forEach(el => {
    resizeObserver.observe(el);
  });

  // Handle getHeight requests from parent
  const messageHandler = (event: MessageEvent<ResizeMessage>) => {
    try {
      // Only process getHeight messages
      if (event.data?.type !== 'getHeight') return;
      
      // If elementId is provided, only process messages for this element
      if (elementId && event.data.elementId !== elementId) {
        return;
      }
      
      console.log('Received getHeight request, calculating height...');
      calculateAndSendHeight();
    } catch (error) {
      console.error('Error handling getHeight message:', error);
    }
  };

  // Add event listener for messages from parent
  window.addEventListener('message', messageHandler);

  // Initial height calculation
  calculateAndSendHeight();

  // Also send height after fonts and images are loaded
  if (document.readyState === 'complete') {
    calculateAndSendHeight();
  } else {
    window.addEventListener('load', calculateAndSendHeight, { once: true });
  }

  // Return cleanup function
  return () => {
    resizeObserver.disconnect();
    window.removeEventListener('message', messageHandler);
    window.removeEventListener('load', calculateAndSendHeight);
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
  };
}

// Auto-initialize if data-auto-resize attribute is present on the script tag
if (typeof document !== 'undefined') {
  const currentScript = document.currentScript as (HTMLScriptElement & {
    dataset: {
      minHeight?: string;
      offset?: string;
      elementId?: string;
    };
  }) | null;

  if (currentScript && currentScript.hasAttribute('data-auto-resize')) {
    const minHeight = currentScript.dataset.minHeight 
      ? parseInt(currentScript.dataset.minHeight, 10) 
      : undefined;
      
    const offset = currentScript.dataset.offset 
      ? parseInt(currentScript.dataset.offset, 10) 
      : undefined;
    
    const elementId = currentScript.dataset.elementId;
    
    setupIframeContentResize({
      minHeight,
      offset,
      elementId
    });
  }
}
